import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SideCart from "../side-cart-view/sideCartView";
import SideDrawer from "../Side-Drawer/SideDrawer";
import BranchesDrawer from "../Branches-Drawer/BranchesDrawer";
import "./Header.css";
import { getCart, subscribeToCartUpdates, CartItem } from '../../Services/cartService';
import { useAuth } from '../../Contexts/AuthContext';

// ─── Scroll Header Animation Styles ──────────────────────────────────────────
const headerScrollStyles = `
  /* ── Base header shell ── */
  .header-v2 {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    will-change: transform, background, box-shadow;
  }

  /* ── Desktop nav container transitions ── */
  .container-menu-desktop {
    transition:
      background 0.4s cubic-bezier(0.16, 1, 0.3, 1),
      box-shadow 0.4s cubic-bezier(0.16, 1, 0.3, 1),
      backdrop-filter 0.4s cubic-bezier(0.16, 1, 0.3, 1),
      border-bottom-color 0.4s cubic-bezier(0.16, 1, 0.3, 1),
      padding 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    border-bottom: 1px solid transparent;
  }

  /* ── Scrolled state — frosted glass ── */
  .container-menu-desktop.header--scrolled {
    background: rgba(255, 255, 255, 0.88) !important;
    backdrop-filter: blur(18px) saturate(1.4);
    -webkit-backdrop-filter: blur(18px) saturate(1.4);
    box-shadow:
      0 1px 0 rgba(113, 127, 224, 0.08),
      0 4px 24px rgba(0, 0, 0, 0.07),
      0 1px 3px rgba(0, 0, 0, 0.04);
    border-bottom-color: rgba(113, 127, 224, 0.12);
  }

  /* ── Logo transition ── */
  .logo img {
    transition: height 0.4s cubic-bezier(0.16, 1, 0.3, 1),
                opacity 0.4s ease;
  }
  .header--scrolled .logo img {
    height: 34px !important;
  }

  /* ── Nav link micro-animations on scroll ── */
  .container-menu-desktop .main-menu > li > a {
    transition: color 0.3s ease, letter-spacing 0.4s ease;
  }
  .header--scrolled .main-menu > li > a {
    letter-spacing: 0.01em;
  }

  /* ── Slim progress bar at very top ── */
  .scroll-progress-bar {
    position: fixed;
    top: 0;
    left: 0;
    height: 2px;
    width: 0%;
    background: linear-gradient(90deg, #717fe0, #a78bfa, #717fe0);
    background-size: 200% 100%;
    z-index: 1001;
    animation: progressShimmer 2s linear infinite;
    opacity: 0;
    transition: opacity 0.3s ease, width 0.08s linear;
  }
  .scroll-progress-bar.visible {
    opacity: 1;
  }
  @keyframes progressShimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* ── Hide-on-scroll-down / show-on-scroll-up ── */
  .header-v2 {
    transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .header-v2.header--hidden {
    transform: translateY(-105%);
  }

  /* ── Announcement bar: permanently hidden ── */
  .header-announcement {
    display: none !important;
  }

  /* ── Icon wrapper scale on scroll ── */
  .wrap-icon-header .icon-header-item {
    transition: transform 0.3s ease, color 0.3s ease;
  }
  .header--scrolled .wrap-icon-header .icon-header-item:hover {
    transform: scale(1.1);
  }

  /* ── Browse label fade out on scroll ── */
  .browse-label {
    transition: opacity 0.35s ease, max-width 0.35s ease, margin 0.35s ease;
    overflow: hidden;
    max-width: 80px;
  }
  .header--scrolled .browse-label {
    opacity: 0;
    max-width: 0;
    margin-left: 0;
  }

  /* ── Active nav indicator morphs on scroll ── */
  .main-menu > li.active-menu > a::after {
    transition: width 0.4s ease, background 0.4s ease;
  }

  /* ── Mobile header: compact on scroll ── */
  .wrap-header-mobile {
    transition:
      padding 0.4s cubic-bezier(0.16, 1, 0.3, 1),
      background 0.4s cubic-bezier(0.16, 1, 0.3, 1),
      box-shadow 0.4s cubic-bezier(0.16, 1, 0.3, 1),
      backdrop-filter 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    border-bottom: 1px solid transparent;
  }
  .wrap-header-mobile.header--scrolled {
    background: rgba(255, 255, 255, 0.88) !important;
    backdrop-filter: blur(18px) saturate(1.4);
    -webkit-backdrop-filter: blur(18px) saturate(1.4);
    box-shadow:
      0 1px 0 rgba(113, 127, 224, 0.08),
      0 4px 20px rgba(0, 0, 0, 0.06);
    border-bottom-color: rgba(113, 127, 224, 0.12);
  }
  .wrap-header-mobile.header--scrolled .logo-mobile img {
    height: 28px !important;
    transition: height 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  /* ── Shadow ripple on cart badge when updated ── */
  @keyframes badgePop {
    0%   { transform: scale(1); }
    40%  { transform: scale(1.35); }
    70%  { transform: scale(0.9); }
    100% { transform: scale(1); }
  }
  .badge--pop {
    animation: badgePop 0.4s cubic-bezier(0.36, 0.07, 0.19, 0.97);
  }

  /* ── Respect reduced-motion preference ── */
  @media (prefers-reduced-motion: reduce) {
    .header-v2,
    .container-menu-desktop,
    .logo img,
    .browse-label,
    .header-announcement,
    .scroll-progress-bar,
    .wrap-header-mobile {
      transition: none !important;
      animation: none !important;
    }
  }
`;

// ─── Hook: Scroll-aware header behaviour ─────────────────────────────────────
function useScrollHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden]     = useState(false);
  const [progress, setProgress] = useState(0);

  const lastScrollY      = useRef(0);
  const ticking          = useRef(false);
  const accumulatedDown  = useRef(0);

  const SCROLL_THRESHOLD  = 60;
  const HIDE_THRESHOLD    = 200;
  const SCROLL_DOWN_DELTA = 80;

  useEffect(() => {
    const docH = () =>
      document.documentElement.scrollHeight - document.documentElement.clientHeight;

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        const y     = window.scrollY;
        const delta = y - lastScrollY.current;

        setScrolled(y > SCROLL_THRESHOLD);

        const total = docH();
        setProgress(total > 0 ? Math.min(100, (y / total) * 100) : 0);

        if (y > HIDE_THRESHOLD) {
          if (delta > 0) {
            accumulatedDown.current += delta;
            if (accumulatedDown.current > SCROLL_DOWN_DELTA) setHidden(true);
          } else {
            accumulatedDown.current = 0;
            setHidden(false);
          }
        } else {
          accumulatedDown.current = 0;
          setHidden(false);
        }

        lastScrollY.current = y;
        ticking.current     = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return { scrolled, hidden, progress };
}

// ─── Hook: Badge pop animation on cart change ────────────────────────────────
function useBadgePop(cartCount: number) {
  const [popping, setPopping] = useState(false);
  const prevCount = useRef(cartCount);

  useEffect(() => {
    if (cartCount !== prevCount.current) {
      setPopping(true);
      const t = setTimeout(() => setPopping(false), 420);
      prevCount.current = cartCount;
      return () => clearTimeout(t);
    }
  }, [cartCount]);

  return popping;
}

// ─── Header Component ─────────────────────────────────────────────────────────
const Header: React.FC = () => {
  const [showSearch, setShowSearch]         = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [cartVisible, setCartVisible]       = useState(false);
  const [showCart, setShowCart]             = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showDrawer, setShowDrawer]         = useState(false);
  const [showBranches, setShowBranches]     = useState(false);
  const [cartItems, setCartItems]           = useState<CartItem[]>([]);

  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, customerProfile } = useAuth();

  const logoPath      = "/asset/images/logo/logo.png";
  const closeIconPath = "/asset/images/icons/icon-close2.png";

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const { scrolled, hidden, progress } = useScrollHeader();
  const badgePopping = useBadgePop(cartCount);

  useEffect(() => {
    setCartItems(getCart());
    const unsubscribe = subscribeToCartUpdates((updatedCart) => {
      setCartItems(updatedCart);
    });
    return unsubscribe;
  }, []);

  // ── Close menu/drawer on route change ────────────────────────────────────
  useEffect(() => {
    setShowMobileMenu(false);
    setShowDrawer(false);
    setShowBranches(false);
  }, [location.pathname]);

  // ── Global event: any component can open the drawer ──────────────────────
  // Usage anywhere: window.dispatchEvent(new Event('open-side-drawer'))
  useEffect(() => {
    const handler = () => setShowDrawer(true);
    window.addEventListener('open-side-drawer', handler);
    return () => window.removeEventListener('open-side-drawer', handler);
  }, []);

  const isActive = (path: string) =>
    location.pathname === path || (path === "/" && location.pathname === "/");

  const openCart = () => {
    setCartVisible(true);
    setTimeout(() => setShowCart(true), 10);
  };

  const closeCart = () => {
    setShowCart(false);
    setTimeout(() => setCartVisible(false), 300);
  };

  const openDrawer  = () => setShowDrawer(true);
  const closeDrawer = () => setShowDrawer(false);

  const openBranches  = () => setShowBranches(true);
  const closeBranches = () => setShowBranches(false);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    setShowSearch(false);
    setSearchQuery('');
    navigate(`/shop?search=${encodeURIComponent(trimmed)}`);
  };

  const badgeStyle: React.CSSProperties = {
    position:       "absolute",
    top:            "-8px",
    right:          "-8px",
    background:     "#717fe0",
    color:          "#fff",
    borderRadius:   "50%",
    fontSize:       "11px",
    fontWeight:     "600",
    minWidth:       "18px",
    height:         "18px",
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    padding:        "0 4px",
    lineHeight:     1,
  };

  const avatarStyle: React.CSSProperties = {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    objectFit: "cover",
    display: "block",
    border: "1.5px solid #e6e6e6",
  };

  return (
    <div>
      <style>{headerScrollStyles}</style>

      {/* ── Scroll Progress Bar ── */}
      <div
        className={`scroll-progress-bar ${progress > 0.5 ? 'visible' : ''}`}
        style={{ width: `${progress}%` }}
        aria-hidden="true"
      />

      <header className={`header-v2${hidden ? ' header--hidden' : ''}`}>

        {/* ── Announcement bar (hidden via CSS) ── */}
        <div className="header-announcement" aria-hidden="true" />

        {/* ── Desktop Header ── */}
        <div className={`container-menu-desktop trans-03${scrolled ? ' header--scrolled' : ''}`}>
          <div className="wrap-menu-desktop">
            <nav className="limiter-menu-desktop p-l-45">
              <Link to="/" className="logo">
                <img
                  src={logoPath}
                  alt="IMG-LOGO"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "https://via.placeholder.com/150x40?text=LOGO";
                  }}
                />
              </Link>

              <div className="menu-desktop">
                <ul className="main-menu">
                  <li className={isActive("/")        ? "active-menu" : ""}><Link to="/">Home</Link></li>
                  <li className={isActive("/shop")    ? "active-menu" : ""}><Link to="/shop">Shop</Link></li>
                  <li className={isActive("/blog")    ? "active-menu" : ""}><Link to="/blog">Blog</Link></li>
                  <li>
                    <a
                      href="#branches"
                      onClick={(e) => { e.preventDefault(); openBranches(); }}
                    >
                      Branches
                    </a>
                  </li>
                  <li className={isActive("/about")   ? "active-menu" : ""}><Link to="/about">About</Link></li>
                  <li className={isActive("/contact") ? "active-menu" : ""}><Link to="/contact">Contact</Link></li>
                </ul>
              </div>

              <div className="wrap-icon-header flex-w flex-r-m h-full">
                {/* Browse */}
                <div className="flex-c-m h-full p-r-18">
                  <div
                    className="icon-header-item cl2 hov-cl1 trans-04 p-lr-11 browse-trigger"
                    onClick={openDrawer}
                    title="Browse Categories & Brands"
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="12" x2="15" y2="12" />
                      <line x1="3" y1="18" x2="18" y2="18" />
                    </svg>
                    <span className="browse-label">Browse</span>
                  </div>
                </div>

                {/* Search */}
                <div className="flex-c-m h-full p-r-24">
                  <div
                    className="icon-header-item cl2 hov-cl1 trans-04 p-lr-11"
                    onClick={() => setShowSearch(true)}
                  >
                    <i className="zmdi zmdi-search"></i>
                  </div>
                </div>

                {/* Account */}
                <div className="flex-c-m h-full p-r-24">
                  <Link
                    to={currentUser ? "/account" : "/login"}
                    className="icon-header-item cl2 hov-cl1 trans-04 p-lr-11"
                    title={currentUser ? "My Account" : "Sign In"}
                  >
                    {currentUser && customerProfile?.photoURL ? (
                      <img src={customerProfile.photoURL} alt="My Account" style={avatarStyle} />
                    ) : (
                      <i className="zmdi zmdi-account"></i>
                    )}
                  </Link>
                </div>

                {/* Cart */}
                <div className="flex-c-m h-full p-l-18 p-r-25 bor5">
                  <div
                    className="icon-header-item cl2 hov-cl1 trans-04 p-lr-11"
                    onClick={openCart}
                    style={{ position: "relative" }}
                  >
                    <i className="zmdi zmdi-shopping-cart"></i>
                    {cartCount > 0 && (
                      <span style={badgeStyle} className={badgePopping ? 'badge--pop' : ''}>
                        {cartCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </div>

        {/* ── Mobile Header ── */}
        <div className={`wrap-header-mobile${scrolled ? ' header--scrolled' : ''}`}>
          <div className="logo-mobile">
            <Link to="/">
              <img
                src={logoPath}
                alt="LOGO"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "https://via.placeholder.com/120x30?text=LOGO";
                }}
              />
            </Link>
          </div>

          <div className="wrap-icon-header flex-w flex-r-m h-full m-r-15">
            {/* Browse */}
            <div className="flex-c-m h-full p-r-10">
              <div
                className="icon-header-item cl2 hov-cl1 trans-04 p-lr-11"
                onClick={openDrawer}
                title="Browse Categories & Brands"
                style={{ cursor: 'pointer' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="15" y2="12" />
                  <line x1="3" y1="18" x2="18" y2="18" />
                </svg>
              </div>
            </div>

            {/* Search */}
            <div className="flex-c-m h-full p-r-10">
              <div
                className="icon-header-item cl2 hov-cl1 trans-04 p-lr-11"
                onClick={() => setShowSearch(true)}
              >
                <i className="zmdi zmdi-search"></i>
              </div>
            </div>

            {/* Account */}
            <div className="flex-c-m h-full p-r-10">
              <Link
                to={currentUser ? "/account" : "/login"}
                className="icon-header-item cl2 hov-cl1 trans-04 p-lr-11"
                title={currentUser ? "My Account" : "Sign In"}
              >
                {currentUser && customerProfile?.photoURL ? (
                  <img src={customerProfile.photoURL} alt="My Account" style={avatarStyle} />
                ) : (
                  <i className="zmdi zmdi-account"></i>
                )}
              </Link>
            </div>

            {/* Cart */}
            <div className="flex-c-m h-full p-lr-10 bor5">
              <div
                className="icon-header-item cl2 hov-cl1 trans-04 p-lr-11"
                onClick={openCart}
                style={{ position: "relative" }}
              >
                <i className="zmdi zmdi-shopping-cart"></i>
                {cartCount > 0 && (
                  <span style={badgeStyle} className={badgePopping ? 'badge--pop' : ''}>
                    {cartCount}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Hamburger */}
          <div
            className={`btn-show-menu-mobile hamburger hamburger--squeeze ${showMobileMenu ? "is-active" : ""}`}
            onClick={() => setShowMobileMenu(prev => !prev)}
            role="button"
            aria-label="Open navigation menu"
            aria-expanded={showMobileMenu}
          >
            <span className="hamburger-box">
              <span className="hamburger-inner"></span>
            </span>
          </div>
        </div>

        {/* ── Mobile Menu ── */}
        {showMobileMenu && (
          <div className="menu-mobile show-menu-mobile">
            <ul className="main-menu-m">
              <li><Link to="/"        onClick={() => setShowMobileMenu(false)} className={isActive("/")        ? "active-menu-m" : ""}>Home</Link></li>
              <li><Link to="/shop"    onClick={() => setShowMobileMenu(false)} className={isActive("/shop")    ? "active-menu-m" : ""}>Shop</Link></li>
              <li><Link to="/blog"    onClick={() => setShowMobileMenu(false)} className={isActive("/blog")    ? "active-menu-m" : ""}>Blog</Link></li>
              <li><a href="#branches" onClick={(e) => { e.preventDefault(); setShowMobileMenu(false); openBranches(); }}>Branches</a></li>
              <li><Link to="/about"   onClick={() => setShowMobileMenu(false)} className={isActive("/about")   ? "active-menu-m" : ""}>About</Link></li>
              <li><Link to="/contact" onClick={() => setShowMobileMenu(false)} className={isActive("/contact") ? "active-menu-m" : ""}>Contact</Link></li>
              <li>
                <Link
                  to={currentUser ? "/account" : "/login"}
                  onClick={() => setShowMobileMenu(false)}
                  className={isActive(currentUser ? "/account" : "/login") ? "active-menu-m" : ""}
                >
                  {currentUser ? "My Account" : "Sign In"}
                </Link>
              </li>
            </ul>
          </div>
        )}
      </header>

      {/* ── Side Drawer ── */}
      <SideDrawer isOpen={showDrawer} onClose={closeDrawer} />

      {/* ── Branches Drawer ── */}
      <BranchesDrawer isOpen={showBranches} onClose={closeBranches} />

      {/* ── Modal Search ── */}
      {showSearch && (
        <div className="modal-search-header flex-c-m trans-04 show-modal-search">
          <div className="container-search-header">
            <button
              className="flex-c-m btn-hide-modal-search trans-04"
              onClick={() => { setShowSearch(false); setSearchQuery(''); }}
            >
              <img
                src={closeIconPath}
                alt="CLOSE"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cline x1='18' y1='6' x2='6' y2='18'%3E%3C/line%3E%3Cline x1='6' y1='6' x2='18' y2='18'%3E%3C/line%3E%3C/svg%3E";
                }}
              />
            </button>

            <form className="wrap-search-header flex-w p-l-15" onSubmit={handleSearchSubmit}>
              <button className="flex-c-m trans-04" type="submit">
                <i className="zmdi zmdi-search"></i>
              </button>
              <input
                className="plh3"
                type="text"
                placeholder="Search products and press Enter..."
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
          </div>
        </div>
      )}

      {/* ── Cart Sidebar ── */}
      {cartVisible && (
        <div
          className={`wrap-header-cart ${showCart ? "show-header-cart" : ""}`}
          onClick={closeCart}
        >
          <div
            className={`header-cart ${showCart ? "slide-in" : "slide-out"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <SideCart onClose={closeCart} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;